import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faFilter } from "@fortawesome/pro-light-svg-icons";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { PaginationContainer } from "src/components/ListScreen";
import { Pagination } from "src/components/Pagination";
import { setPreviousURLTeamMembers } from "src/redux/actions/openedPages";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import { colors } from "src/styles";
import { Client, DBLocation, ID, TeamMember } from "src/types";
import styled from "styled-components";
import { BooleanParam, NumberParam, StringParam, useQueryParam, useQueryParams } from "use-query-params";
import TeamMembersFilter, { FiltersArguments, RoleFilter } from "./TeamMembersFilter";
import TeamMembersTable from "./TeamMembersTable";
import { teamMemberstableHeaders } from "./constants";

const TeamMembers = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const dispatch = useDispatch();
  const [openedPage, setOpenedPage] = useQueryParam("openedPage", NumberParam);
  const { id: managerId } = useSelector(getUserData);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  const [error, setError] = useState("");

  const [query, setQuery] = useQueryParams({
    openedPage: NumberParam,
    onSite: BooleanParam,
    name: StringParam,
    employeeId: StringParam,
    client: StringParam,
    location: StringParam,
    role: StringParam,
  });

  const isFiltersQuery = Object.entries(query).filter(([key, value]) => {
    if (key === "openedPage") {
      return false;
    }
    return !!value;
  }).length;

  const [isFilterBoxOpen, setIsFilterBoxOpen] = useState(false);
  const [areFiltersApplied, setAreFiltersApplied] = useState(!!isFiltersQuery);

  const [pageInfo, setPageInfo] = useState({ total: 0, perPage: 10 });

  const [locationFiltersData, setLocationFiltersData] = useState<DBLocation[]>();
  const [clientFiltersData, setClientFiltersData] = useState<Client[]>();
  const [roleFilterData, setRoleFilterData] = useState<RoleFilter>();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  const onRowClick = (id: ID) => {
    dispatch(setPreviousURLTeamMembers(search));
    navigate(`/team-members/${id}`);
  };

  const onPageChangeClick = (pageNumber: number) => {
    setOpenedPage(pageNumber);
    setQuery({
      ...query,
      openedPage: pageNumber,
    });
  };

  const onCloseFilters = () => setIsFilterBoxOpen(false);

  const getFiltersData = async () => {
    setIsLoadingFilters(true);
    setError("");

    try {
      const [clientsData, rolesFilter, locationsData] = await Promise.allSettled([
        FirebaseDataService.getClientsByManagerId({ managerId }),
        FirebaseDataService.getRolesFilter(),
        FirebaseDataService.getLocationsByManagerId({ managerId }),
      ]);

      setClientFiltersData(clientsData.status === "fulfilled" ? clientsData.value.data : []);
      setRoleFilterData(rolesFilter.status === "fulfilled" ? rolesFilter.value.data : null);
      setLocationFiltersData(locationsData.status === "fulfilled" ? locationsData.value.data : null);
    } catch (err) {
      setLocationFiltersData([]);
      setError(err.message);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const getTeamMembers = async () => {
    setIsLoading(true);
    setError("");

    try {
      const filtersForDb = {
        ...(query.client && { client: query.client }),
        ...(query.employeeId && { employeeId: query.employeeId }),
        ...(query.onSite && { onSite: query.onSite }),
        ...(query.location && { location: query.location }),
        ...(query.name && { name: query.name }),
        ...(query.role && { role: query.role }),
      };
      const { data, total, perPage } = await FirebaseDataService.getTeamMembers({
        managerId,
        currentTeamMembersPage: openedPage || 1,
        filters: filtersForDb,
      });

      setTeamMembers(data || []);
      setPageInfo({ total, perPage });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onAppliedHandler = async ({
    clientFilter,
    employeeIdFilter,
    isOnSite,
    locationFilter,
    nameFilter,
    roleFilter,
  }: FiltersArguments) => {
    setAreFiltersApplied(true);
    setIsLoading(true);
    setError("");

    try {
      const URLQuery = {
        ...query,
        openedPage: "1",
        client: clientFilter || undefined,
        employeeId: employeeIdFilter || undefined,
        onSite: isOnSite || undefined,
        location: locationFilter || undefined,
        name: nameFilter || undefined,
        role: roleFilter || undefined,
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const urlQueryWithoutUndefined = Object.fromEntries(Object.entries(URLQuery).filter(([_, value]) => value));

      const filtersForDb = {
        ...(clientFilter && { client: clientFilter }),
        ...(employeeIdFilter && { employeeId: employeeIdFilter }),
        ...(isOnSite && { onSite: isOnSite }),
        ...(locationFilter && { location: locationFilter }),
        ...(nameFilter && { name: nameFilter }),
        ...(roleFilter && { role: roleFilter }),
      };

      const { data, total, perPage } = await FirebaseDataService.getTeamMembers({
        managerId,
        currentTeamMembersPage: 1,
        filters: filtersForDb,
      });
      setTeamMembers(data);
      setPageInfo({ total, perPage });
      setQuery(urlQueryWithoutUndefined);

      onCloseFilters();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const clearAllURLFilterParams = () => {
    setQuery({
      openedPage: undefined,
      client: undefined,
      employeeId: undefined,
      onSite: undefined,
      location: undefined,
      name: undefined,
      role: undefined,
    });
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { openedPage, ...rest } = query;
    if (!Object.values(rest).filter((item) => item).length) {
      setAreFiltersApplied(false);
    }
  }, [query]);

  useEffect(() => {
    getFiltersData();
  }, []);

  useEffect(() => {
    getTeamMembers();
  }, [query]);

  const totalMembers = pageInfo.total || teamMembers.length;
  const onSiteCount = teamMembers.filter((member) => member.onSite && Object.keys(member.onSite).length).length;
  const uniqueClientCount = new Set(teamMembers.flatMap((member) => member.clients.map((client) => client.id))).size;
  const multiRoleCount = teamMembers.filter((member) => Object.keys(member.roles || {}).length > 1).length;

  const resultsMessage =
    !areFiltersApplied && !teamMembers.length
      ? "No Team Members assigned"
      : "We couldn't find anything matching your filters. Please, try again with different filter criteria.";

  return (
    <Page>
      <Header>
        <Eyebrow>Resources</Eyebrow>
        <TitleRow>
          <Title>Team Members</Title>
          <Meta>{`${totalMembers} team member${totalMembers === 1 ? "" : "s"} in scope`}</Meta>
        </TitleRow>
      </Header>

      <TilesGrid>
        <SummaryCard>
          <SummaryLabel>Total Team Members</SummaryLabel>
          <SummaryValue>{totalMembers}</SummaryValue>
          <SummarySub>matching current view</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>On Site</SummaryLabel>
          <SummaryValue>{onSiteCount}</SummaryValue>
          <SummarySub>on the current page</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Assigned Clients</SummaryLabel>
          <SummaryValue>{uniqueClientCount}</SummaryValue>
          <SummarySub>represented on this page</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Multi-Role</SummaryLabel>
          <SummaryValue>{multiRoleCount}</SummaryValue>
          <SummarySub>members with multiple roles</SummarySub>
        </SummaryCard>
      </TilesGrid>

      <ActionRow>
        <FilterButton disabled={isFilterBoxOpen} onClick={() => setIsFilterBoxOpen(true)} type="button">
          <FilterIcon>{(faFilter as IconDefinition).iconName === "filter" ? "Filter" : "Filter"}</FilterIcon>
        </FilterButton>
      </ActionRow>

      {(isFilterBoxOpen || areFiltersApplied) && (
        <FilterWrapper>
          <TeamMembersFilter
            onAppliedHandler={onAppliedHandler}
            setAreFiltersApplied={setAreFiltersApplied}
            areFiltersApplied={areFiltersApplied}
            isFilterBoxOpen={isFilterBoxOpen}
            onCloseFilters={onCloseFilters}
            clientFiltersData={clientFiltersData || []}
            roleFilterData={roleFilterData}
            initialLocationFiltersData={locationFiltersData || []}
            clearAllURLFilterParams={clearAllURLFilterParams}
          />
        </FilterWrapper>
      )}

      {error && !isLoadingFilters && !isLoading && <ErrorBanner>{error}</ErrorBanner>}

      <Panel>
        <PanelHeader>
          <PanelTitle>All Team Members</PanelTitle>
          <PanelMeta>{`${teamMembers.length} shown`}</PanelMeta>
        </PanelHeader>

        {isLoadingFilters || isLoading ? (
          <EmptyRow>Loading...</EmptyRow>
        ) : !teamMembers.length ? (
          <EmptyRow>{resultsMessage}</EmptyRow>
        ) : (
          <TeamMembersTable
            headers={teamMemberstableHeaders}
            onRowClick={onRowClick}
            data={teamMembers}
            roleFilterData={roleFilterData}
          />
        )}
      </Panel>

      {!!teamMembers.length && (
        <PaginationContainer>
          <Pagination
            totalCount={pageInfo.total}
            pageSize={pageInfo.perPage}
            onPageChange={onPageChangeClick}
            currentPage={openedPage || 1}
          />
        </PaginationContainer>
      )}
    </Page>
  );
};

const Page = styled.div`
  padding: 24px 40px 0;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
`;

const Title = styled.h1`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 32px;
  font-weight: 700;
`;

const Meta = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
  padding: 16px;
`;

const SummaryLabel = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const SummaryValue = styled.p`
  margin: 6px 0 2px;
  color: ${colors.black.dark1};
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
`;

const SummarySub = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const FilterButton = styled.button`
  border: 0;
  background: ${colors.black.dark1};
  color: ${colors.white.default};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 10px 16px;
  cursor: pointer;

  &:disabled {
    background: ${colors.grey.dark6};
    cursor: not-allowed;
  }
`;

const FilterIcon = styled.span`
  display: inline-flex;
  align-items: center;
`;

const FilterWrapper = styled.div`
  margin-bottom: 16px;
`;

const ErrorBanner = styled.p`
  margin: 0 0 16px;
  color: ${colors.red.default};
  font-size: 13px;
  font-weight: 600;
`;

const Panel = styled.section`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${colors.grey.light3};
  padding: 12px 16px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const PanelMeta = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const EmptyRow = styled.div`
  padding: 20px 16px;
  color: ${colors.grey.dark6};
  font-size: 13px;
`;

export default TeamMembers;
